import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { login as loginRequest } from "../api/authService";
import { getExpectedUserType } from "../utils/authScope";

const AuthContext = createContext(null);

const STORAGE_KEY = "bigmarket_auth";
const TOKEN_KEY = "bigmarket_token";

function readStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [storedUser, setUser] = useState(readStoredAuth);
  const [loading, setLoading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (storedUser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedUser));
      localStorage.setItem(TOKEN_KEY, storedUser.token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [storedUser]);

  const expectedUserType = getExpectedUserType(location.pathname);
  const user = storedUser && storedUser.userType === expectedUserType ? storedUser : null;

  async function login(email, password) {
    setLoading(true);
    try {
      const data = await loginRequest(email, password);
      setUser(data);
      return data;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setUser(null);
  }

  // For flows (OTP verify, dev-login) where the auth payload is already fetched
  // and just needs to be stored, unlike `login` which performs the request itself.
  function setSession(data) {
    setUser(data);
  }

  function updateUser(partialUser) {
    setUser((prev) => (prev ? { ...prev, ...partialUser } : prev));
  }

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    setSession,
    updateUser,
    loginModalOpen,
    openLoginModal: () => setLoginModalOpen(true),
    closeLoginModal: () => setLoginModalOpen(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
